import { FiCheck } from "react-icons/fi";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { formatCents } from "@/lib/utils";
import {
  IA_STEPS,
  type CachedDetermination,
  type StepDef,
  type WorkflowStep,
} from "@/features/workflows/types";

interface Props {
  currentStep: WorkflowStep;
  onSelect: (step: WorkflowStep) => void;
  determination: CachedDetermination | null;
  /** Ordered steps for this workflow (defaults to the IA step list). */
  steps?: StepDef[];
  /** Rail heading (defaults to the IA flow's name). */
  title?: string;
}

/**
 * The persistent wizard rail: ordered steps with completion ticks + the current
 * step highlighted, plus a determination summary chip (IA type + est. monthly
 * payment) once step 6 has run. Steps are freely navigable. Reused across the
 * resolution workflows by passing its own `steps`/`title` (the determination
 * chip only renders when a determination is supplied — none for Excise).
 */
export function WorkflowRail({
  currentStep,
  onSelect,
  determination,
  steps = IA_STEPS,
  title = "Installment Agreement",
}: Props) {
  const activeIdx = Math.max(0, steps.findIndex((s) => s.key === currentStep));

  return (
    <Card className="gap-4 p-4">
      <div className="flex items-center justify-between gap-3">
        <h3 className="font-semibold">{title}</h3>
        <DeterminationChip determination={determination} />
      </div>
      <ol className="grid gap-1">
        {steps.map((step, i) => {
          const isCurrent = step.key === currentStep;
          const isDone = i < activeIdx;
          return (
            <li key={step.key}>
              <button
                type="button"
                onClick={() => onSelect(step.key)}
                aria-current={isCurrent ? "step" : undefined}
                className={cn(
                  "flex w-full items-center gap-3 rounded-md px-3 py-2 text-left text-sm transition-colors",
                  isCurrent
                    ? "bg-primary/10 text-foreground font-medium"
                    : "text-muted-foreground hover:bg-muted",
                )}
              >
                <span
                  className={cn(
                    "grid size-6 shrink-0 place-items-center rounded-full border text-xs",
                    isDone
                      ? "border-primary bg-primary text-primary-foreground"
                      : isCurrent
                        ? "border-primary text-primary"
                        : "border-muted-foreground/40",
                  )}
                >
                  {isDone ? <FiCheck className="size-3.5" /> : i + 1}
                </span>
                {step.label}
              </button>
            </li>
          );
        })}
      </ol>
    </Card>
  );
}

function DeterminationChip({ determination }: { determination: CachedDetermination | null }) {
  if (!determination?.available || !determination.iaType) return null;
  const monthly =
    determination.monthlyPaymentCents != null
      ? formatCents(determination.monthlyPaymentCents)
      : null;
  return (
    <Badge variant={determination.expired ? "destructive" : "secondary"} className="whitespace-nowrap">
      {determination.iaTypeLabel || determination.iaType}
      {monthly ? ` · ${monthly}/mo` : ""}
    </Badge>
  );
}
